import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, map, filter, take, firstValueFrom } from 'rxjs';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { PermissionService } from './permission.service';
import { FirebaseService } from './firebase.service';
import { StorageService } from './storage.service';
import { Client } from '../types/multi-property.types';

// Helper function to create a properly structured Client object with sensible defaults
function createClientWithDefaults(partial: Partial<Client> & { id: string; name: string }): Client {
  const now = Timestamp.now();
  return {
    id: partial.id,
    name: partial.name,
    logoUrl: partial.logoUrl || '',
    logoWhiteUrl: partial.logoWhiteUrl,
    brandColor: partial.brandColor || '#000000',
    fontColor: partial.fontColor || '#FFFFFF',
    themeConfig: partial.themeConfig || {},
    contactInfo: partial.contactInfo || {
      corporateEmail: 'info@example.com', // Default email, should be updated during proper onboarding
      headquartersAddress: '123 Default St, City, ST 12345' // Default address, required for CAN-SPAM
    },
    settings: {
      timezone: 'UTC',
      locale: 'en-US',
      currency: 'USD',
      ...partial.settings // Override with any provided settings (including databaseId)
    },
    domain: partial.domain,
    status: partial.status || 'active',
    createdAt: partial.createdAt || now,
    updatedAt: partial.updatedAt || now
  };
}

// Use centralized Client interface - no more local interfaces!

@Injectable({
  providedIn: 'root'
})
export class ClientContextService {
  private permissionService = inject(PermissionService);
  private firebaseService = inject(FirebaseService);
  private storageService = inject(StorageService);

  private selectedClientId$ = new BehaviorSubject<string | null>(null);
  private availableClients$ = new BehaviorSubject<Client[]>([]);
  private lastUserId: string | null = null; // Track user changes for logout detection
  private isInitialized = false; // Track if we've processed the first auth state

  constructor() {
    // Load persisted client selection immediately to prevent redirect to selection page
    this.loadPersistedSelection();

    // Listen for auth changes to reset/update available clients
    // IMPORTANT: Filter out null claims to avoid treating page refresh as login/logout
    this.permissionService.userClaims$.pipe(
      filter(claims => claims !== null)
    ).subscribe(claims => {
      // For user tracking, we'll use a combination of role and clientId as a "user identifier"
      // since UserClaims doesn't have uid. This works for detecting role/client changes.
      const currentUserIdentifier = `${claims.role}:${claims.clientId || 'none'}`;

      // Check if this is a user change (login/logout)
      const isUserChange = this.lastUserId !== null && this.lastUserId !== currentUserIdentifier;

      // On first auth load, just set the user identifier without treating it as a change
      if (this.lastUserId === null) {
        this.lastUserId = currentUserIdentifier;
        console.debug('[ClientContext] Initial auth loaded:', currentUserIdentifier);
      } else if (isUserChange) {
        // Actual user change detected (different user logged in)
        console.debug('[ClientContext] User change detected:', this.lastUserId, '->', currentUserIdentifier);
        this.setSelectedClient(null);
        this.lastUserId = currentUserIdentifier;
      }

      if (claims?.role === 'super_admin') {
        // For super admins, load available clients and preserve persisted selection
        this.loadAvailableClients();
        // The persisted selection was already loaded in constructor, so just keep it
        // Don't force them to reselect unless it's a login/logout event
      } else if (claims?.clientId) {
        // Regular user - load their client document from Firestore
        const firstClientId = claims.clientId;

        // CRITICAL: Clear both selectedClientId and availableClients before loading
        // This prevents race conditions where services react to a persisted selectedClientId
        // while availableClients is still empty (not persisted across page refreshes)
        this.selectedClientId$.next(null);
        this.availableClients$.next([]);

        // Load the client document, then set selection
        this.loadClientForRegularUser(firstClientId).then(() => {
          this.setSelectedClient(firstClientId);
        }).catch(error => {
          console.error('Failed to load client for regular user:', error);
          // Even on error, set the selection so the app isn't stuck
          this.setSelectedClient(firstClientId);
        });
      } else {
        // No claims or no access - clear everything
        this.setSelectedClient(null);
        this.availableClients$.next([]);
      }
    });
  }

  // Public observables
  get selectedClientId(): Observable<string | null> {
    return this.selectedClientId$.asObservable();
  }

  get availableClients(): Observable<Client[]> {
    return this.availableClients$.asObservable();
  }

  // Combined observable for components that need both
  get clientContext(): Observable<{ selectedClientId: string | null; availableClients: Client[] }> {
    return combineLatest([
      this.selectedClientId$,
      this.availableClients$
    ]).pipe(
      map(([selectedClientId, availableClients]) => ({
        selectedClientId,
        availableClients
      }))
    );
  }

  // Check if current user needs to select a client
  get needsClientSelection(): Observable<boolean> {
    return combineLatest([
      this.permissionService.userClaims$,
      this.selectedClientId$
    ]).pipe(
      map(([claims, selectedClientId]) => {
        if (!claims) return false;

        // Super admins need to select a client if none is selected
        if (claims.role === 'super_admin') {
          return !selectedClientId;
        }

        // Regular users don't need to select (auto-assigned)
        return false;
      })
    );
  }

  // Actions
  setSelectedClient(clientId: string | null): void {
    this.selectedClientId$.next(clientId);
    this.persistSelection(clientId);
  }

  // Clear client selection (useful for logout or when switching users)
  clearSelection(): void {
    this.setSelectedClient(null);
  }

  // Force clear on logout - can be called explicitly by auth service
  clearOnLogout(): void {
    console.debug('[ClientContext] Clearing client selection on logout');
    this.setSelectedClient(null);
    this.availableClients$.next([]);
    this.lastUserId = null;
    this.isInitialized = false; // Reset initialization flag for next login
  }

  getCurrentClientId(): string | null {
    return this.selectedClientId$.value;
  }

  getCurrentClient(): Client | null {
    const clientId = this.selectedClientId$.value;
    if (!clientId) return null;

    const clients = this.availableClients$.value;
    const foundClient = clients.find(c => c.id === clientId) || null;

    return foundClient;
  }

  /**
   * Get the database ID for the currently selected client
   * Used for hybrid architecture database context switching
   */
  getCurrentClientDatabaseId(): string | undefined {
    const currentClient = this.getCurrentClient();
    const selectedClientId = this.getCurrentClientId();

    return currentClient?.settings?.databaseId;
  }

  /**
   * Check if the current client uses a specific database (for context switching)
   */
  isClientUsingDatabase(databaseId?: string): boolean {
    const currentDatabaseId = this.getCurrentClientDatabaseId();

    // Both undefined = using default database
    if (!currentDatabaseId && !databaseId) return true;

    // Direct match
    return currentDatabaseId === databaseId;
  }

  // For regular users, get their effective client ID
  getEffectiveClientId(): string | null {
    const claims = this.permissionService.getCurrentClaims();

    if (!claims) return null;

    // Super admin uses selected client
    if (claims.role === 'super_admin') {
      return this.getCurrentClientId();
    }

    // Regular user uses their first assigned client
    return claims.clientId || null;
  }

  /**
   * Force refresh the available clients list (useful after client onboarding)
   */
  async refreshAvailableClients(): Promise<void> {
    console.debug('[ClientContext] Refreshing available clients...');
    await this.loadAvailableClients();
  }

  /**
   * Wait until the client context is fully initialized and ready for use.
   * This is useful for components that need to ensure a client is selected before proceeding.
   *
   * For super admins: Waits until both a client is selected AND available clients are loaded
   * For regular users: Waits until their auto-assigned client is available
   *
   * @returns Promise that resolves when the client context is ready
   *
   * @example
   * async ngOnInit() {
   *   await this.clientContextService.waitUntilReady();
   *   // Now safe to proceed with operations requiring client context
   *   this.loadData();
   * }
   */
  async waitUntilReady(): Promise<void> {
    return firstValueFrom(
      this.clientContext.pipe(
        filter(({ selectedClientId, availableClients }) => {
          const isSuperAdmin = this.permissionService.getCurrentClaims()?.role === 'super_admin';
          return isSuperAdmin
            ? !!(selectedClientId && availableClients.length > 0)
            : !!selectedClientId;
        }),
        take(1)
      )
    ).then(() => {});
  }

  // Get client display name by ID
  getClientDisplayName(clientId: string): string {
    const clients = this.availableClients$.value;
    const client = clients.find(c => c.id === clientId);
    if (client) {
      return client.name;
    }

    // Fallback to known client names that match database
    const knownClients: Record<string, string> = {
      'blackfire': 'Black Fire Casino',
      'morongo': 'Morongo Casino Resort & Spa'
    };

    return knownClients[clientId] || clientId;
  }

  private async loadAvailableClients(): Promise<void> {
    try {
      // Try to load clients from CENTRAL database (hybrid architecture)
      // Super admins need to see ALL clients regardless of Portal's database configuration
      try {
        const centralDb = this.firebaseService.getCentralDatabase();
        const clientsSnapshot = await getDocs(collection(centralDb, 'clients'));
        const clients: Client[] = [];

        clientsSnapshot.forEach(doc => {
          const data = doc.data();

          // Check for databaseId in multiple possible locations
          const databaseId = data['databaseId'] || data['settings']?.['databaseId'] || undefined;

          console.debug('[ClientContext] Loading client', doc.id,  {
            name: data['name'],
            databaseId: databaseId,
            rootDatabaseId: data['databaseId'],
            settingsDatabaseId: data['settings']?.['databaseId'],
            hasDbIdAtRoot: 'databaseId' in data,
            hasSettings: !!data['settings'],
            settingsKeys: data['settings'] ? Object.keys(data['settings']) : [],
            allFieldNames: Object.keys(data)
          });

          clients.push(createClientWithDefaults({
            id: doc.id,
            name: data['name'] || doc.id,
            domain: data['domain'],
            logoUrl: data['logoUrl'],
            logoWhiteUrl: data['logoWhiteUrl'],
            brandColor: data['brandColor'],
            fontColor: data['fontColor'],
            themeConfig: data['themeConfig'],
            contactInfo: data['contactInfo'],
            settings: {
              ...data['settings'],
              databaseId: databaseId // Ensure databaseId is in settings
            },
            status: data['status'],
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt']
          }));
        });

        if (clients.length > 0) {
          console.debug('[ClientContext] Loaded', clients.length, 'clients from central database');
          this.availableClients$.next(clients);
          return;
        }
      } catch (error) {
        console.warn('Could not load clients from central database, falling back to known clients:', error);
      }

      // Fallback to minimal known clients - these should be properly onboarded
      const fallbackClients: Client[] = [
        createClientWithDefaults({
          id: 'blackfire',
          name: 'Blackfire Entertainment',
          brandColor: '#000000',
          settings: {
            databaseId: 'blackfire-isolated',
            timezone: 'America/Los_Angeles',
            locale: 'en-US',
            currency: 'USD'
          }
        }),
        createClientWithDefaults({
          id: 'morongo',
          name: 'Morongo Casino Resort & Spa',
          brandColor: '#8B4513',
          settings: {
            databaseId: 'morongo-isolated',
            timezone: 'America/Los_Angeles',
            locale: 'en-US',
            currency: 'USD'
          }
        })
      ];

      this.availableClients$.next(fallbackClients);

      // Super admins should manually select a client, don't auto-select
      // If there's a persisted selection, it will already be loaded in constructor
    } catch (error) {
      console.error('Failed to load available clients:', error);
      this.availableClients$.next([]);
    }
  }

  /**
   * Load a single client document for a regular user
   * This ensures we get the full client configuration including settings.databaseId
   */
  private async loadClientForRegularUser(clientId: string): Promise<void> {
    try {
      // Load the actual client document from central database
      const centralDb = this.firebaseService.getCentralDatabase();
      const clientDocRef = doc(centralDb, 'clients', clientId);
      const clientDoc = await getDoc(clientDocRef);

      if (!clientDoc.exists()) {
        // Client not found, use fallback
        console.warn(`Client ${clientId} not found in Firestore, using fallback`);
        const userClients: Client[] = [createClientWithDefaults({
          id: clientId,
          name: this.getClientName(clientId),
        })];
        this.availableClients$.next(userClients);
        return;
      }

      // Parse the client document
      const data = clientDoc.data();

      // Check for databaseId in multiple possible locations
      const databaseId = data['databaseId'] || data['settings']?.['databaseId'] || undefined;

      const client = createClientWithDefaults({
        id: clientDoc.id,
        name: data['name'] || clientDoc.id,
        domain: data['domain'],
        logoUrl: data['logoUrl'],
        logoWhiteUrl: data['logoWhiteUrl'],
        brandColor: data['brandColor'],
        fontColor: data['fontColor'],
        themeConfig: data['themeConfig'],
        contactInfo: data['contactInfo'],
        settings: {
          ...data['settings'],
          databaseId: databaseId // Ensure databaseId is in settings
        },
        status: data['status'],
        createdAt: data['createdAt'],
        updatedAt: data['updatedAt']
      });

      this.availableClients$.next([client]);
    } catch (error) {
      console.error('Failed to load client for regular user:', error);
      // Fallback to stub client
      const userClients: Client[] = [createClientWithDefaults({
        id: clientId,
        name: this.getClientName(clientId),
      })];
      this.availableClients$.next(userClients);
    }
  }

  private getClientName(clientId: string): string {
    // Simple mapping for real clients in the database
    const clientNames: Record<string, string> = {
      'blackfire': 'Blackfire Entertainment',
      'morongo': 'Morongo Casino Resort & Spa'
    };

    return clientNames[clientId] || clientId;
  }

  private loadPersistedSelection(): void {
    try {
      const saved = this.storageService.getSync('selectedClientId');
      if (saved) {
        this.selectedClientId$.next(saved);
        console.debug('[ClientContext] Loaded persisted client selection:', saved);
      }
    } catch (error) {
      console.warn('Failed to load persisted client selection:', error);
    }
  }

  private persistSelection(clientId: string | null): void {
    try {
      if (clientId) {
        this.storageService.setSync('selectedClientId', clientId);
        console.debug('[ClientContext] Persisted client selection:', clientId);
      } else {
        this.storageService.removeSync('selectedClientId');
        console.debug('[ClientContext] Cleared persisted client selection');
      }
    } catch (error) {
      console.warn('Failed to persist client selection:', error);
    }
  }
}
